import { emptyMask } from "domains/canvas/components/sections/AIPanel/mask";
import { CanvasStore, ImagePolotnoElement } from "domains/canvas/interfaces";

import { getRectangleAroundNonTransparentPixels } from "./getRectangleAroundNonTransparentPixel";
import { maskImageId } from "./maskImageId";

const errorBackgroundColor = "transparent";
const warningBackgroundColor = "transparent";
interface RectSvgOptions {
  stroke?: string;
  fill?: string;
  width?: number;
  height?: number;
  strokeWidth?: number;
}

export const contextZoneConfig = {
  sdxl: {
    max: 1_344,
    min: 64,
    margin: 24,
  },
  sd: {
    max: 1_024,
    min: 64,
    margin: 24,
  },
  flux: {
    max: 2_048,
    min: 64,
    margin: 24,
  },
  fluxSchnell: {
    max: 2_048,
    min: 256,
    margin: 24,
  },
  fluxPro: {
    max: 2_048,
    min: 64,
    margin: 24,
  },
  fluxPro1_1: {
    max: 2_048,
    min: 64,
    margin: 24,
  },
  fluxPro1_1Ultra: {
    max: 3_136,
    min: 64,
    margin: 24,
  },
};

function createRectSvgDataUrl(options: RectSvgOptions): string {
  const {
    stroke = "black",
    fill = "white",
    width = 512,
    height = 512,
    strokeWidth = 5,
  } = options;
  const rectSvg = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="${fill}" xmlns="http://www.w3.org/2000/svg"><rect width="${width}" height="${height}" stroke="${stroke}" stroke-width="${strokeWidth}"/></svg>`;
  return `data:image/svg+xml;base64,${btoa(rectSvg)}`;
}

export const contextZoneId = "ai-context-zone";

/** The api only accepts multiples of 8 */
export const makeNumberAMultipleOfEight = (n: number) => {
  return Math.ceil(n / 8) * 8;
};

// TODO: hide it instead so we can change section and keep the properties
export const addContextZone = (store: CanvasStore) => {
  if (!store.activePage) return;

  void store.history.ignore(async () => {
    const contextZone = store.getElementById(contextZoneId);
    const errorOrWarning = store.activePage?.custom?.contextZoneError;
    const [backgroundColor, strokeColor] = (() => {
      switch (errorOrWarning) {
        case "error":
          return [errorBackgroundColor, "red"];
        case "warning":
          return [warningBackgroundColor, "yellow"];
        default:
          return ["transparent", "transparent"];
      }
    })();

    if (contextZone) {
      // update the src
      contextZone.set({
        src: createRectSvgDataUrl({
          width: contextZone.width,
          height: contextZone.height,
          fill: backgroundColor,
          stroke: strokeColor,
        }),
      });

      return;
    }

    store.activePage?.addElement({
      type: "svg",
      x: 0,
      y: 0,
      width: store.width,
      height: store.height,
      id: contextZoneId,
      src: createRectSvgDataUrl({
        width: store.width,
        height: store.height,
        fill: backgroundColor,
        stroke: strokeColor,
      }),
      name: "Context Zone",

      custom: {
        layers: {
          hidePosition: true,
          hideVisible: true,
          hideLock: true,
          hideDelete: true,
          disableRename: true,
        },
      },

      keepRatio: false,
      // can user select element?
      // if false, element will be "invisible" for user clicks
      selectable: false,
      // use for absolute positing of element
      alwaysOnTop: true,
      // also we can hide some elements from the export
      showInExport: false,
      // can element be moved and rotated
      draggable: false,
      // can we change content of element?
      contentEditable: false,
      // can we remove element from UI with button or keyboard?
      removable: false,
      // can we resize element?
      resizable: false,
      // can we change style of element?
      styleEditable: false,
    });
  });
};

export const removeContextZone = (store: CanvasStore) => {
  if (!store.activePage) return;

  const ids = store.activePage?.children.filter((el) => {
    return el.id === contextZoneId;
  });

  store.deleteElements(ids?.map((el) => el.id) as any);
};

export const smartPositionContextZone = async (store: CanvasStore) => {
  await store.history.ignore(async () => {
    const contextZone = store.getElementById(contextZoneId);
    const maskElement = store.getElementById(maskImageId) as
      | ImagePolotnoElement
      | undefined;

    addContextZone(store);

    if (!maskElement) {
      contextZone?.set({
        x: 0,
        y: 0,
        width: store.width,
        height: store.height,
      });
      return;
    }

    // TODO: optimize the zone here by increasing the margin until we get a minimum of 512 or that less than 30% of the pixels are transparent
    // eslint-disable-next-line prefer-const
    let { x, y, width, height } = await getRectangleAroundNonTransparentPixels(
      maskElement.src,
      {
        overflow: false,
        margin: 0,
      }
    );
    // Add the offset of the mask to the context zone
    x += maskElement.x;
    y += maskElement.y;

    if (!maskElement.src || maskElement.src === emptyMask) {
      return contextZone?.set({
        visible: true,
        showInExport: false,
        styleEditable: false,

        x: 0,
        y: 0,
        width: store.width,
        height: store.height,
      });
    }

    // now that we have x,y,width and height of the contour of the mask, we can find the square that contains it with a margin of 200
    // Set the margin around the context zone
    const margin = 200;

    // Determine the size of the context zone, which is the maximum of the width and height
    const size = Math.max(width, height);

    // Calculate the new X and Y coordinates for the context zone, ensuring they are not less than 0
    let newX = Math.max(0, x - margin);
    let newY = Math.max(0, y - margin);

    // Calculate the new width and height for the context zone, ensuring they do not exceed the store's width and height
    const newW = makeNumberAMultipleOfEight(
      Math.min(store.width, size + 2 * margin)
    );
    const newH = makeNumberAMultipleOfEight(
      Math.min(store.height, size + 2 * margin)
    );

    // Calculate the distance from the right and bottom of the store
    // If the context zone exceeds the store's dimensions, these values will be greater than 0
    const distanceFromRight = newX + newW - store.width;
    const distanceFromBottom = newY + newH - store.height;

    // If the context zone exceeds the store's dimensions, adjust the X and Y coordinates accordingly
    if (distanceFromRight > 0) {
      newX -= distanceFromRight;
    }
    if (distanceFromBottom > 0) {
      newY -= distanceFromBottom;
    }

    // Set the properties of the context zone
    contextZone?.set({
      visible: true,
      showInExport: false,
      draggable: false,
      resizable: false,
      styleEditable: false,
      selectable: false,
      x: newX,
      y: newY,
      width: newW,
      height: newH,
    });
  });
};

type ContextZoneType = "error" | "warning" | "ok";

export const setContextZone = (store: CanvasStore, type: ContextZoneType) => {
  void store.history.ignore(async () => {
    store.activePage?.set({
      custom: {
        ...store.activePage.custom,
        contextZoneError: type,
      },
    });
    addContextZone(store);
  });
};
